/**
 * Auth store — access/refresh tokens + the current `User` object.
 *
 * Persists access token + refresh token + minimal user in `localStorage`
 * so a page reload doesn't bounce to /login. Sensitive: store ONLY what's
 * already in the JWT (id, email, role, is_active). Never password / keys.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export type UserStatus = 'pending' | 'active' | 'suspended' | 'rejected'

export interface AuthUser {
  id: number
  email: string | null              // sub-accounts have no email (PULSE-47)
  role: string
  status?: UserStatus              // Phase 3 — surfaced by /auth/me
  is_active: boolean
  has_api_keys: boolean
  telegram_enabled: boolean
  telegram_chat_id: string | null
  max_concurrent_positions: number
  daily_loss_limit_pct: number
  bybit_testnet: boolean
  created_at: string
  last_login_at: string | null
  // PULSE-47
  master_user_id?: number | null
  account_label?: string | null
  is_master?: boolean
}

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: AuthUser | null
  /** PULSE-47 — current account scope: 'all' (master view) or sub_id string */
  selectedAccount: string

  /** "Оставаться в сети" — when false, the session is dropped on browser
   *  close (enforced on boot via a sessionStorage marker). */
  rememberMe: boolean

  setAuth(tokens: { access: string; refresh: string }, user: AuthUser): void
  /** Token-only update (refresh flow) — preserves selectedAccount. */
  setTokens(tokens: { access: string; refresh: string }, user: AuthUser): void
  setRememberMe(v: boolean): void
  setUser(user: AuthUser): void
  setSelectedAccount(v: string): void
  clear(): void

  // Derived selectors — kept here so components don't re-implement
  isAuthenticated(): boolean
  isActive(): boolean
  isAdmin(): boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      selectedAccount: 'all',
      rememberMe: true,

      setAuth({ access, refresh }, user) {
        set({ accessToken: access, refreshToken: refresh, user, selectedAccount: 'all' })
      },
      setTokens({ access, refresh }, user) {
        set({ accessToken: access, refreshToken: refresh, user })
      },
      setRememberMe(v) {
        set({ rememberMe: v })
      },
      setUser(user) {
        set({ user })
      },
      setSelectedAccount(v) {
        set({ selectedAccount: v })
      },
      clear() {
        set({ accessToken: null, refreshToken: null, user: null, selectedAccount: 'all' })
      },

      isAuthenticated() {
        return !!get().accessToken
      },
      isActive() {
        return !!get().user?.is_active
      },
      isAdmin() {
        return get().user?.role === 'admin'
      },
    }),
    {
      name: 'pulsar-auth',
      storage: createJSONStorage(() => localStorage),
      // Only persist the persistent bits — selectors are recomputed.
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        user: s.user,
        selectedAccount: s.selectedAccount,
        rememberMe: s.rememberMe,
      }),
    },
  ),
)

/** Drop a non-remembered session when the browser was fully closed.
 *  localStorage keeps the token (so reloads stay logged in), but a
 *  sessionStorage marker is wiped on browser close — so a fresh browser
 *  session with rememberMe=false starts logged out. Call once on boot. */
export function enforceRememberPolicy(): void {
  const SESSION_MARKER = 'pulsar-session-alive'
  try {
    const fresh = !sessionStorage.getItem(SESSION_MARKER)
    const s = useAuthStore.getState()
    if (fresh && s.accessToken && !s.rememberMe) {
      s.clear()
    }
    sessionStorage.setItem(SESSION_MARKER, '1')
  } catch {
    /* sessionStorage unavailable (private mode quirk) — leave session as-is */
  }
}
